import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Search, type LucideIcon } from 'lucide-react';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
} from '@proerp/ui';
import { describeError } from '@/lib/apiClient';
import { useSearch, featureRoute } from '@/features/search/searchApi';
import { RESOURCE_ICON, RESOURCE_LABEL, type SearchResourceTypeUi } from '@/features/search/searchMeta';
import type { NavGroup, NavItem } from './nav';
import { matchLeaves, type NavLeaf } from './navLeaves';
import type { RecentPlace } from './recentPlaces';
import { useKeyboardInset } from './useKeyboardInset';

/** Treffer je Datensatz-Gruppe im Sheet — mehr zeigt „Alle Treffer". */
const HITS_PER_GROUP = 3;
const MODULE_HIT_LIMIT = 5;

/** Kurz warten, bis der Nutzer zu tippen aufhört, statt je Taste den Server zu fragen. */
function useDebounced(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export interface WohinSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  navItems: NavItem[];
  groupOrder: NavGroup[];
  leaves: NavLeaf[];
  pinned: NavLeaf[];
  recents: RecentPlace[];
  /** Ein gewählter Datensatz (Suchtreffer) wird für „Zuletzt geöffnet" gemerkt. */
  onPickRecord: (place: { path: string; label: string; detail: string; resourceType: SearchResourceTypeUi }) => void;
}

const ROW =
  'flex min-h-12 w-full select-none touch-manipulation items-center gap-3 rounded-lg px-3 py-2 text-left text-[15px] text-foreground transition-colors active:bg-muted hover:bg-muted/60';
const SECTION = 'px-1 pb-1.5 pt-4 text-[0.6875rem] font-medium uppercase tracking-[0.05em] text-muted-foreground';

function RowIcon({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
      <Icon className="size-4" strokeWidth={1.5} aria-hidden />
    </span>
  );
}

/**
 * „Wohin?" — die Navigation der mobilen Leiste (Variante B, Tech-Lead
 * 2026-09-24). Statt fester Reiter: angeheftete Module, zuletzt Geöffnetes und
 * alle Bereiche als Kacheln. Das Suchfeld sitzt UNTEN (Daumenbereich) und findet
 * Module sofort und Datensätze über die globale Suche (ADR 0015/0069).
 */
export function WohinSheet({ open, onOpenChange, navItems, groupOrder, leaves, pinned, recents, onPickRecord }: WohinSheetProps) {
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState<NavGroup | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const keyboardInset = useKeyboardInset(open && isTyping);
  const debounced = useDebounced(query, 200);
  const search = useSearch(open ? debounced : '');
  const trimmed = query.trim();

  // Nächstes Öffnen startet sauber — keine stehengebliebene Suche, kein offener Bereich.
  useEffect(() => {
    if (open) return;
    setQuery('');
    setGroup(null);
    setIsTyping(false);
  }, [open]);

  const grouped = useMemo(
    () =>
      groupOrder
        .map((name) => ({ name, items: navItems.filter((item) => item.group === name) }))
        .filter((entry) => entry.items.length > 0),
    [groupOrder, navItems],
  );
  const moduleHits = useMemo(() => matchLeaves(leaves, query).slice(0, MODULE_HIT_LIMIT), [leaves, query]);
  const recordGroups = search.data?.data.groups.filter((entry) => entry.hits.length > 0) ?? [];
  const close = () => onOpenChange(false);

  function renderSearch() {
    const isSearching = search.isFetching || debounced !== query;
    return (
      <>
        {moduleHits.length > 0 && (
          <section aria-label="Module">
            <h3 className={SECTION}>Module</h3>
            {moduleHits.map((leaf) => (
              <Link key={leaf.to} to={leaf.to} onClick={close} className={ROW}>
                <RowIcon icon={leaf.icon} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{leaf.label}</span>
                  <span className="block truncate text-xs text-muted-foreground">{leaf.parentLabel ?? leaf.group}</span>
                </span>
              </Link>
            ))}
          </section>
        )}
        {recordGroups.map((entry) => (
          <section key={entry.type} aria-label={entry.label}>
            <h3 className={SECTION}>{entry.label}</h3>
            {entry.hits.slice(0, HITS_PER_GROUP).map((hit) => {
              const path = featureRoute(hit.type, hit.id);
              return (
                <Link
                  key={`${hit.type}:${hit.id}`}
                  to={path}
                  onClick={() => {
                    onPickRecord({ path, label: hit.title, detail: RESOURCE_LABEL[hit.type], resourceType: hit.type });
                    close();
                  }}
                  className={ROW}
                >
                  <RowIcon icon={RESOURCE_ICON[hit.type]} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{hit.title}</span>
                    {hit.subtitle && <span className="block truncate text-xs text-muted-foreground">{hit.subtitle}</span>}
                  </span>
                </Link>
              );
            })}
          </section>
        ))}
        {search.isError && (
          <p className="px-3 py-4 text-sm text-danger-500" role="alert">
            Suche nicht erreichbar: {describeError(search.error)}
          </p>
        )}
        {!isSearching && !search.isError && moduleHits.length === 0 && recordGroups.length === 0 && (
          <p className="px-3 py-10 text-center text-sm text-muted-foreground">Nichts gefunden für „{trimmed}".</p>
        )}
        {isSearching && recordGroups.length === 0 && (
          <p className="px-3 py-4 text-sm text-muted-foreground" role="status">
            Suche läuft …
          </p>
        )}
        {recordGroups.length > 0 && (
          <Link to={`/suche?q=${encodeURIComponent(trimmed)}`} onClick={close} className={cn(ROW, 'mt-2 font-medium')}>
            <span className="flex-1">Alle Treffer für „{trimmed}"</span>
            <ArrowRight className="size-4 shrink-0" strokeWidth={1.5} aria-hidden />
          </Link>
        )}
      </>
    );
  }

  function renderGroup(name: NavGroup) {
    const entry = grouped.find((candidate) => candidate.name === name);
    return (
      <>
        <button type="button" onClick={() => setGroup(null)} className="-ml-1 flex h-11 items-center gap-1 px-1 text-sm font-medium text-foreground">
          <ChevronLeft className="size-4" strokeWidth={1.5} aria-hidden />
          Alle Bereiche
        </button>
        <h3 className={cn(SECTION, 'pt-1')}>{name}</h3>
        {entry?.items.map((item) => (
          <div key={item.to}>
            <Link to={item.to} onClick={close} className={ROW}>
              <RowIcon icon={item.icon} />
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              {!item.ready && <span className="text-xs text-muted-foreground">bald</span>}
            </Link>
            {item.children
              ?.filter((child) => child.to !== item.to)
              .map((child) => (
                <Link key={child.to} to={child.to} onClick={close} className={cn(ROW, 'min-h-11 pl-14 text-sm text-muted-foreground')}>
                  {child.label}
                </Link>
              ))}
          </div>
        ))}
      </>
    );
  }

  function renderHome() {
    return (
      <>
        <section aria-label="Angeheftet">
          <h3 className={SECTION}>Angeheftet</h3>
          <div className="grid grid-cols-4 gap-2">
            {pinned.map((leaf) => (
              <Link
                key={leaf.to}
                to={leaf.to}
                onClick={close}
                aria-label={leaf.label}
                className="flex min-h-[4.5rem] flex-col items-center justify-center gap-1.5 rounded-xl bg-muted/60 px-1 text-xs font-medium text-foreground active:bg-muted"
              >
                <leaf.icon className="size-5" strokeWidth={1.5} aria-hidden />
                <span className="max-w-full truncate">{leaf.short}</span>
              </Link>
            ))}
          </div>
        </section>
        {recents.length > 0 && (
          <section aria-label="Zuletzt geöffnet">
            <h3 className={SECTION}>Zuletzt geöffnet</h3>
            {recents.map((place) => {
              const moduleLeaf = place.resourceType ? undefined : leaves.find((leaf) => leaf.to === place.path);
              const icon = place.resourceType ? RESOURCE_ICON[place.resourceType] : (moduleLeaf?.icon ?? Search);
              return (
                <Link key={place.path} to={place.path} onClick={close} className={ROW}>
                  <RowIcon icon={icon} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{place.label}</span>
                    <span className="block truncate text-xs text-muted-foreground">{place.detail}</span>
                  </span>
                </Link>
              );
            })}
          </section>
        )}
        <section aria-label="Alle Bereiche">
          <h3 className={SECTION}>Alle Bereiche</h3>
          <div className="grid grid-cols-2 gap-2">
            {grouped.map((entry) => {
              const Icon = entry.items[0].icon;
              return (
                <button
                  key={entry.name}
                  type="button"
                  onClick={() => setGroup(entry.name)}
                  className="flex min-h-[5.25rem] flex-col items-start gap-1.5 rounded-xl bg-muted/60 p-3 text-left active:bg-muted"
                >
                  <Icon className="size-4 text-foreground" strokeWidth={1.5} aria-hidden />
                  <span className="text-sm font-semibold leading-tight text-foreground">{entry.name}</span>
                  <span className="flex w-full items-center justify-between text-xs text-muted-foreground">
                    {entry.items.length === 1 ? '1 Modul' : `${entry.items.length} Module`}
                    <ChevronRight className="size-3.5" strokeWidth={1.5} aria-hidden />
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      </>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        // Höhe über denselben `data-[side=bottom]`-Selektor wie das Sheet selbst — dessen
        // `h-auto` ist spezifischer und schlägt ein schlichtes `h-[88svh]` (sonst wächst das
        // Sheet über den Bildschirm, der Kopf ist weg und die Liste scrollt nicht).
        className="flex flex-col gap-0 overflow-hidden rounded-t-2xl p-0 data-[side=bottom]:h-[88svh] tablet:hidden"
        style={keyboardInset ? { paddingBottom: keyboardInset } : undefined}
      >
        <SheetHeader className="px-4 pb-1 pt-4 text-left">
          <SheetTitle className="text-md font-semibold text-foreground">Wohin?</SheetTitle>
          <SheetDescription className="sr-only">Modul, Projekt oder Beleg suchen und öffnen.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto overscroll-contain px-3 pb-3">
          {trimmed ? renderSearch() : group ? renderGroup(group) : renderHome()}
        </div>
        <div className="shrink-0 border-t border-border bg-background px-3 pt-2.5 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          <InputGroup className="h-11">
            <InputGroupAddon>
              <Search strokeWidth={1.5} aria-hidden />
            </InputGroupAddon>
            <InputGroupInput
              type="search"
              enterKeyHint="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onFocus={() => setIsTyping(true)}
              onBlur={() => setIsTyping(false)}
              placeholder="Projekt, Beleg oder Modul"
              aria-label="Projekt, Beleg oder Modul suchen"
              className="text-base"
            />
          </InputGroup>
        </div>
      </SheetContent>
    </Sheet>
  );
}
